import { TransactionCoaFilterType } from "@common/helpers/transactions";
import { Class, ClassFilterType } from "@common/types/class";
import { TransactionReviewField } from "@common/types/transactionFieldReview";
import { TransactionsFilters } from "../filters/lib";
import { reviewActionDropsFromFilter } from "./buildBulkFieldReviewRequests";
import type { BulkUpdateParams } from "./lib";

type BulkUpdateField = BulkUpdateParams["update"];

type BulkUpdateValues = {
    [Params in BulkUpdateParams as Params["update"]]: Params["value"];
};

export type ClassesById = ReadonlyMap<string, Pick<Class, "rootClassId">>;

interface LeavesFiltersContext {
    filters: TransactionsFilters;
    classesById: ClassesById;
}

type LeavesFilters<Field extends BulkUpdateField> = (
    value: BulkUpdateValues[Field],
    context: LeavesFiltersContext,
) => boolean;

interface ReferenceFilters {
    ids: string[] | undefined;
    hasValue: boolean | undefined;
    withEmpty: boolean | undefined;
}

function isOutsideList<T>(list: readonly T[] | undefined, value: T): boolean {
    return !!list?.length && !list.includes(value);
}

function leavesReferenceFilters(
    value: string | null,
    { ids, hasValue, withEmpty }: ReferenceFilters,
): boolean {
    if (ids?.length && (value === null || !ids.includes(value))) {
        return true;
    }
    if (hasValue && withEmpty) {
        return false;
    }
    return value === null ? !!hasValue : !!withEmpty;
}

function leavesClassFilters(
    classIds: string[],
    {
        filters: { classes, classFilterType = ClassFilterType.ANY_SELECTED },
        classesById,
    }: LeavesFiltersContext,
): boolean {
    if (classFilterType === ClassFilterType.NO_CLASSES) {
        return classIds.length > 0;
    }
    if (classFilterType === ClassFilterType.HAS_CLASSES) {
        return classIds.length === 0;
    }
    if (!classes?.length) {
        return false;
    }
    const matchedCount = classIds.filter((id) => classes.includes(id)).length;
    if (classFilterType === ClassFilterType.EXCLUDE_SELECTED) {
        return matchedCount > 0;
    }
    if (classFilterType === ClassFilterType.ALL_SELECTED) {
        const filterRootIds = new Set(
            classes.map((id) => classesById.get(id)?.rootClassId ?? id),
        );
        return matchedCount !== filterRootIds.size;
    }
    return matchedCount === 0;
}

/**
 * Whether every row a bulk update touches stops matching `filters`, keyed by
 * update field. Each entry mirrors the backend predicate in
 * `TransactionsQueryService` for the filter keys that field can change, and
 * answers true only when the new value alone decides that the row leaves.
 * Filters that also depend on other data (journal entry lines, required
 * actions recomputed by the backend, review records) answer false.
 */
const UPDATE_LEAVES_FILTERS: {
    [Field in BulkUpdateField]: LeavesFilters<Field>;
} = {
    entity: (entity, { filters: { entityIds } }) =>
        isOutsideList(entityIds, entity.id),
    categoryId: (categoryId, { filters: { category } }) =>
        isOutsideList(category, categoryId),
    memo: (memo, { filters: { hasNote, noNote, requiresNote } }) =>
        !requiresNote &&
        leavesReferenceFilters(memo === "" ? null : memo, {
            ids: undefined,
            hasValue: hasNote,
            withEmpty: noNote,
        }),
    counterpartyId: (
        counterpartyId,
        {
            filters: {
                counterpartiesIds,
                hasCounterparty,
                withEmptyOrUnverifiedCounterparty,
            },
        },
    ) =>
        leavesReferenceFilters(counterpartyId, {
            ids: counterpartiesIds,
            hasValue: hasCounterparty,
            withEmpty: withEmptyOrUnverifiedCounterparty,
        }),
    customerId: (
        customerId,
        { filters: { customerIds, hasCustomer, withEmptyCustomer } },
    ) =>
        leavesReferenceFilters(customerId, {
            ids: customerIds,
            hasValue: hasCustomer,
            withEmpty: withEmptyCustomer,
        }),
    classes: leavesClassFilters,
    coa: (accountId, { filters: { coaFilterType } }) =>
        accountId !== null &&
        !!coaFilterType?.length &&
        coaFilterType.every((type) => type === TransactionCoaFilterType.KICK),
    accrualCoa: (
        accountId,
        {
            filters: {
                accrualCoaAccounts,
                includeUnassignedAccrualAccount,
                accrualAccountId,
            },
        },
    ) => {
        if (accrualAccountId && accountId !== accrualAccountId) {
            return true;
        }
        const hasAccounts = !!accrualCoaAccounts?.length;
        if (!hasAccounts && !includeUnassignedAccrualAccount) {
            return false;
        }
        if (accountId === null) {
            return !includeUnassignedAccrualAccount;
        }
        return !accrualCoaAccounts?.includes(accountId);
    },
    excludeFrom1099: (isExcluded, { filters: { excludeFrom1099 } }) =>
        excludeFrom1099 !== undefined && isExcluded !== excludeFrom1099,
    pairedTransferId: (pairedTransferId, { filters: { hasTransferMatch } }) => {
        if (hasTransferMatch === undefined) {
            return false;
        }
        return hasTransferMatch === (pairedTransferId === null);
    },
    taxQuestionAnswers: () => false,
};

/**
 * Review fields the backend marks reviewed for every updated row once the
 * update value is set. CATEGORY and the ledgers are left out: the backend
 * skips uncategorized taxonomies, uncategorized accounts and personal-entity
 * rows (`TransactionFieldReviewService`), so those rows can stay in an
 * unreviewed filter.
 */
const ALWAYS_AUTO_REVIEWED_FIELDS: readonly TransactionReviewField[] = [
    TransactionReviewField.ENTITY,
    TransactionReviewField.CLASS,
    TransactionReviewField.COUNTERPARTY,
    TransactionReviewField.NOTE,
];

function updatedValueLeavesFilters<Field extends BulkUpdateField>(
    field: Field,
    value: BulkUpdateValues[Field],
    context: LeavesFiltersContext,
): boolean {
    return UPDATE_LEAVES_FILTERS[field](value, context);
}

interface BulkUpdateLeavesFiltersParams extends LeavesFiltersContext {
    updateParams: BulkUpdateParams;
    autoReviewField: TransactionReviewField | undefined;
}

export function bulkUpdateLeavesFilters({
    updateParams,
    filters,
    classesById,
    autoReviewField,
}: BulkUpdateLeavesFiltersParams): boolean {
    return (
        updatedValueLeavesFilters(updateParams.update, updateParams.value, {
            filters,
            classesById,
        }) ||
        (autoReviewField !== undefined &&
            ALWAYS_AUTO_REVIEWED_FIELDS.includes(autoReviewField) &&
            reviewActionDropsFromFilter(filters, [autoReviewField], "add"))
    );
}
